<p-dialog
  header="Personalized link"
  [visible]="visible()"
  (visibleChange)="visible.set($event)"
  [modal]="true"
  [style]="{ width: '440px' }"
  appendTo="body"
>
  @if (createdUuid()) {
    <!-- ── Result ──────────────────────────────────────────────────────── -->
    <div class="flex flex-col gap-3 pt-2">
      <p class="text-[0.8rem] text-wb-muted m-0">
        Send this link to the recipient — it resolves to their personalized video.
      </p>
      <div class="flex gap-2">
        <input pInputText class="w-full" [value]="createdUrl()" readonly />
        <p-button icon="pi pi-copy" title="Copy link" (click)="copyUrl()" />
      </div>
    </div>
    <ng-template #footer>
      <div class="flex justify-end gap-2 pt-2">
        <p-button label="Create another" severity="secondary" (click)="createAnother()" />
        <p-button label="Done" (click)="close()" />
      </div>
    </ng-template>
  } @else if (visibleFields().length === 0) {
    <!-- ── Not configured ──────────────────────────────────────────────── -->
    <div class="flex flex-col gap-2 pt-2">
      <p class="text-[0.8rem] text-wb-muted m-0">
        This project has no personalization fields configured.
      </p>
      <p class="text-[0.8rem] text-wb-muted m-0">
        Add a variable named <code class="text-wb-text">PERSONALIZATION_FIELDS</code> in the config panel and set
        its value to the field names, separated by commas — for example
        <code class="text-wb-text">JMENO,PRIJMENI,GENDER</code>.
      </p>
      <p class="text-[0.8rem] text-wb-muted m-0">
        To say outright which fields are images, use JSON instead:
        <code class="text-wb-text">&#123;"JMENO":"text","IMAGE_PHOTO":"image"&#125;</code>. Otherwise any field
        whose name starts with <code class="text-wb-text">IMAGE_</code> gets a file picker.
      </p>
      <p class="text-[0.8rem] text-wb-muted m-0">
        A field can be composed from others instead of typed — give it a
        <code class="text-wb-text">derivedFrom</code> template:
        <code class="text-wb-text">&#123;"FIRST_NAME":"variant","LAST_NAME":"text","TEXT_NAME":&#123;"derivedFrom":"&#123;FIRST_NAME&#125; &#123;LAST_NAME&#125;"&#125;&#125;</code>.
      </p>
    </div>
    <ng-template #footer>
      <div class="flex justify-end pt-2">
        <p-button label="Close" severity="secondary" (click)="close()" />
      </div>
    </ng-template>
  } @else {
    <!-- ── Form ────────────────────────────────────────────────────────── -->
    <form [formGroup]="form()" class="flex flex-col gap-3 pt-2" (ngSubmit)="submit()">
      @for (field of visibleFields(); track field.key) {
        <div>
          <label class="text-[0.75rem] text-wb-muted block mb-1" [attr.for]="'plink-' + field.key">{{ field.label }}</label>
          @if (field.isImage) {
            <!-- File input stays in the DOM but hidden: the browser's native
                 control can't be styled, so a p-button drives it instead. -->
            <input
              type="file"
              accept="image/*"
              class="hidden"
              [id]="'plink-file-' + field.key"
              (change)="onImagePicked(field.key, $event)"
            />

            @if (imagePreviews()[field.key]; as preview) {
              <div class="image-frame">
                <img class="image-frame__img" [src]="preview" [alt]="field.key" />
                <div class="image-frame__bar">
                  <span>~{{ previewSizeKb(field.key) }} kB</span>
                  <div class="flex gap-1">
                    <p-button icon="pi pi-refresh" severity="secondary" text size="small"
                      title="Replace" (click)="pickFile(field.key)" />
                    <p-button icon="pi pi-trash" severity="secondary" text size="small"
                      title="Remove" (click)="clearImage(field.key)" />
                  </div>
                </div>
              </div>
            } @else if (capturingField() === field.key) {
              <div class="image-frame">
                <!-- muted + playsinline so autoplay isn't blocked; mirrored
                     because an un-mirrored self-view reads as backwards. -->
                <video #cameraVideo class="image-frame__img image-frame__img--mirrored" autoplay playsinline muted></video>
                <div class="image-frame__bar">
                  <p-button label="Capture" icon="pi pi-camera" size="small" (click)="capturePhoto(field.key)" />
                  <!-- Not "Cancel": the dialog footer already has one, and that
                       closes the whole dialog rather than just the camera. -->
                  <p-button label="Close camera" severity="secondary" text size="small" (click)="stopCamera()" />
                </div>
              </div>
            } @else {
              <div class="image-empty">
                <i class="pi pi-image image-empty__icon"></i>
                <div class="flex gap-2">
                  <p-button label="Upload" icon="pi pi-upload" severity="secondary" size="small"
                    (click)="pickFile(field.key)" />
                  <p-button label="Take photo" icon="pi pi-camera" severity="secondary" size="small"
                    (click)="startCamera(field.key)" />
                </div>
              </div>
            }
          } @else if (field.options.length > 0) {
            <select
              [id]="'plink-' + field.key"
              [formControlName]="field.key"
              class="w-full bg-[var(--wb-bg-surface)] border border-[var(--wb-border)] rounded-lg text-wb-text text-[0.85rem] px-3 py-2 outline-none"
            >
              <option value="" disabled>— select —</option>
              @for (option of field.options; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          } @else {
            <input pInputText class="w-full" [id]="'plink-' + field.key" [formControlName]="field.key" />
          }
        </div>
      }
      @if (error()) {
        <small class="text-red-400 block">{{ error() }}</small>
      }
    </form>
    <ng-template #footer>
      <div class="flex justify-end gap-2 pt-2">
        <p-button label="Cancel" severity="secondary" (click)="close()" />
        <p-button
          [label]="submitting() ? 'Creating…' : 'Create link'"
          [disabled]="form().invalid || submitting()"
          (click)="submit()"
        />
      </div>
    </ng-template>
  }
</p-dialog>
